// src/pages/IncidentList.js
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Card,
  Button,
  Form,
  Row,
  Col,
  Accordion,
} from "react-bootstrap";
import Select from "react-select";
import Pagination from "../../components/Pagination";

const IncidentList = () => {
  const [records, setRecords] = useState([]);
  const [recordsUnfiltered, setRecordsUnfiltered] = useState([]);

  // Filters
  const [filterAccidentType, setFilterAccidentType] = useState(null);
  const [filterAccidentNo, setFilterAccidentNo] = useState("");
  const [filterDotStatus, setFilterDotStatus] = useState(null);
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterSavedBy, setFilterSavedBy] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // 🔐 Resolve user (same as IncidentReports)
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const userId = isAdmin && paramId ? paramId : localStorage.getItem("userId");

  // Hardcoded accident type options (same as form)
  const accidentTypeOptions = [
    { label: "Accident", value: "Accident" },
    { label: "Claim", value: "Claim" },
    { label: "Hazmat Incident", value: "Hazmat Incident" },
    { label: "Other", value: "Other" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        };
        const res = await axios.get(`${API_BASE}/api/incidents/${userId}`, {
          headers,
        });
        setRecordsUnfiltered(res.data);
        setRecords(res.data);
      } catch (err) {
        console.error("❌ Fetch error", err);
      }
    };
    fetchData();
  }, [API_BASE, userId]);

  // Filtering
  const applyFilters = () => {
    let filtered = [...recordsUnfiltered];

    if (filterAccidentType?.length) {
      const selectedTypes = filterAccidentType.map((t) => t.value);
      filtered = filtered.filter((i) => selectedTypes.includes(i.inc_type));
    }

    if (filterAccidentNo) {
      filtered = filtered.filter((i) =>
        i.inc_number
          ?.toString()
          .toLowerCase()
          .includes(filterAccidentNo.toLowerCase())
      );
    }

    if (filterDotStatus) {
      filtered = filtered.filter(
        (i) =>
          (filterDotStatus === "DOT" && i.inc_dot_reportable === "DOT") ||
          (filterDotStatus === "non-DOT" && i.inc_dot_reportable !== "DOT")
      );
    }

    if (filterDateFrom) {
      filtered = filtered.filter(
        (i) => new Date(i.inc_date) >= new Date(filterDateFrom)
      );
    }

    if (filterDateTo) {
      filtered = filtered.filter(
        (i) => new Date(i.inc_date) <= new Date(filterDateTo)
      );
    }

    if (filterSavedBy?.length) {
      const selectedUsers = filterSavedBy.map((s) => s.value);
      filtered = filtered.filter((i) =>
        selectedUsers.includes(i.saved_by_name)
      );
    }

    setRecords(filtered);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterAccidentType(null);
    setFilterAccidentNo("");
    setFilterDotStatus(null);
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterSavedBy(null);
    setRecords(recordsUnfiltered);
  };

  const paginatedRecords = records.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // dropdown options
  const savedByOptions = [
    ...new Set(recordsUnfiltered.map((i) => i.saved_by_name).filter(Boolean)),
  ].map((s) => ({ label: s, value: s }));

  const handleCancel = () => {
    if (isAdmin && paramId) {
      navigate(`/userhome/${paramId}`);
    } else {
      navigate("/userhome");
    }
  };

  return (
    <Container className="form-wrapper my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">Incidents</h3>
        <div>
          <Button variant="secondary" className="me-2" onClick={handleCancel}>
            Return
          </Button>
          <Button
            variant="success"
            onClick={() => navigate(`/incidents/${userId}/incident-new`)}
          >
            Add
          </Button>
        </div>
      </div>

      <Card>
        <Card.Body>
          {/* 🔽 Collapsible Filters */}
          <Accordion defaultActiveKey="">
            <Accordion.Item eventKey="0">
              <Accordion.Header>Filters</Accordion.Header>
              <Accordion.Body>
                <Form className="mb-4">
                  <Row className="align-items-end g-3">
                    <Col md={3}>
                      <Form.Label>Type of Accident</Form.Label>
                      <Select
                        isMulti
                        options={accidentTypeOptions}
                        value={filterAccidentType}
                        onChange={setFilterAccidentType}
                        placeholder="Select Accident Type(s)"
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Accident No.</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search Accident Number"
                        value={filterAccidentNo}
                        onChange={(e) => setFilterAccidentNo(e.target.value)}
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>DOT Status</Form.Label>
                      <div>
                        <Form.Check
                          inline
                          type="radio"
                          label="DOT"
                          name="dotStatus"
                          checked={filterDotStatus === "DOT"}
                          onChange={() => setFilterDotStatus("DOT")}
                        />
                        <Form.Check
                          inline
                          type="radio"
                          label="non-DOT"
                          name="dotStatus"
                          checked={filterDotStatus === "non-DOT"}
                          onChange={() => setFilterDotStatus("non-DOT")}
                        />
                      </div>
                    </Col>
                    <Col md={2}>
                      <Form.Label>Date From</Form.Label>
                      <Form.Control
                        type="date"
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>Date To</Form.Label>
                      <Form.Control
                        type="date"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Saved By</Form.Label>
                      <Select
                        isMulti
                        options={savedByOptions}
                        value={filterSavedBy}
                        onChange={setFilterSavedBy}
                        placeholder="Select Saved By"
                      />
                    </Col>
                    <Col md={3} className="d-flex gap-2">
                      <Button
                        variant="secondary"
                        onClick={clearFilters}
                        className="mt-3"
                      >
                        Clear Filter
                      </Button>
                      <Button
                        variant="success"
                        onClick={applyFilters}
                        className="mt-3"
                      >
                        Filter
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* 📋 Table */}
          {records.length === 0 ? (
            <p className="mt-3 text-muted fst-italic">No incidents found.</p>
          ) : (
            <div className="table-responsive mt-3">
              <table className="table table-bordered align-middle text-center">
                <thead className="table-light">
                  <tr>
                    <th>Reviewed</th>
                    <th>Preventable</th>
                    <th>DOT</th>
                    <th>Type Of Incident</th>
                    <th>Incident No.</th>
                    <th>Incident Date</th>
                    <th>Date Saved</th>
                    <th>Saved By</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRecords.map((i) => (
                    <tr key={i.inc_id}>
                      <td className="text-center">{i.reviewed}</td>
                      <td className="text-center">{i.inc_preventable}</td>
                      <td className="text-center">{i.inc_dot_reportable}</td>
                      <td className="text-center">{i.inc_type}</td>
                      <td className="text-center">{i.inc_number}</td>
                      <td className="text-center">
                        {i.inc_date
                          ? new Date(i.inc_date).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="text-center">
                        {i.inc_saved_date
                          ? new Date(i.inc_saved_date).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="text-center">{i.saved_by_name || "-"}</td>
                      <td className="text-center">
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() =>
                            navigate(
                              `/incidents/${userId}/incident-detail/${i.inc_id}`
                            )
                          }
                        >
                          ✎ Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Pagination
                currentPage={currentPage}
                totalItems={records.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default IncidentList;

